var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { sum } from "lodash";
import { getPagedId as getPagedIdBase, } from "../../redux/generic/paged.utils";
import { EContractLengthType, EContractPaymentRepetitionType, EContractType, } from "../../types/contract.types";
import { EPropertyEPCLabel } from "../../types/property.types";
import { isEndedContract, isResidentialLease, } from "../../utils/contract.utils";
import { addDays, addMonths, addQuarters, endOfMonth, addYears, endOfDay, isSameDay, startOfMonth, startOfQuarter, startOfYear, subDays, subMonths, isBefore, } from "../../utils/date-fns.utils";
import { formatDateWithCustomClosebyFormat } from "../../utils/date.utils";
import { getLocalizedText } from "../../utils/i18n/i18n.utils";
import { formatCurrency, getVat } from "../../utils/number.utils";
import { isFlemishProperty } from "../../utils/property.utils";
import { EContractFetchType } from "./contract.types";
export var getRenewalDeadline = function (contract) {
    var stopDate = contract.stopDate, contractLengthType = contract.contractLengthType, tacitRenewal = contract.tacitRenewal;
    if (!stopDate || !tacitRenewal) {
        return;
    }
    switch (contractLengthType) {
        case EContractLengthType.Short:
            return subDays(subMonths(addDays(stopDate, 1), 3), 1);
        case EContractLengthType.Long:
            return subDays(subMonths(addDays(stopDate, 1), 6), 1);
        default:
            return;
    }
};
export var getNewDeadlineAfterTacitRenewal = function (contract) {
    var startDate = contract.startDate, stopDate = contract.stopDate, tacitRenewal = contract.tacitRenewal;
    if (!stopDate || !tacitRenewal) {
        return;
    }
    if (isSameDay(addYears(startDate, 1), addDays(stopDate, 1))) {
        return addYears(stopDate, 9 - 1);
    }
    if (isSameDay(addYears(startDate, 2), addDays(stopDate, 1))) {
        return addYears(stopDate, 9 - 2);
    }
    if (isSameDay(addYears(startDate, 3), addDays(stopDate, 1))) {
        return addYears(stopDate, 9 - 3);
    }
    for (var index = 0; index <= 10; index++) {
        if (isSameDay(addYears(startDate, 9 + index * 3), addDays(stopDate, 1))) {
            return addYears(stopDate, 3);
        }
    }
};
export var getRenewalInfo = function (contract) {
    if (isEndedContract(contract)) {
        return;
    }
    var renewalDeadline = getRenewalDeadline(contract);
    var newDeadlineAfterTacitRenewal = getNewDeadlineAfterTacitRenewal(contract);
    if (!renewalDeadline || !newDeadlineAfterTacitRenewal) {
        return;
    }
    return getLocalizedText("contract.tacit_renewal.new_dates_info", {
        renewalDate: formatDateWithCustomClosebyFormat(addDays(renewalDeadline, 1), "d MMM yyyy"),
        newEndDate: formatDateWithCustomClosebyFormat(newDeadlineAfterTacitRenewal, "d MMM yyyy"),
    });
};
export var isValidStopDateForTacitRenewal = function (params) {
    var startDate = params.startDate, stopDate = params.stopDate;
    if (!startDate || !stopDate) {
        return undefined;
    }
    return !!__spreadArray([
        addYears(startDate, 1),
        addYears(startDate, 2),
        addYears(startDate, 3),
        addYears(startDate, 9)
    ], Array(30)
        .fill(0)
        .map(function (_, index) { return addYears(startDate, 9 + (index + 1) * 3); }), true).find(function (x) { return isSameDay(addDays(stopDate, 1), x); });
};
export var getProRataBaseEndDate = function (startDate, contract, includingExtraDay) {
    if (includingExtraDay === void 0) { includingExtraDay = false; }
    if (!contract) {
        return startDate;
    }
    // Get followup
    var followUp = contract.repetitionType || EContractPaymentRepetitionType.Monthly;
    var result = startDate;
    if (followUp === EContractPaymentRepetitionType.Monthly) {
        result = addMonths(startOfMonth(startDate), 1);
    }
    else if (followUp === EContractPaymentRepetitionType.Quarterly) {
        result = addQuarters(startOfQuarter(startDate), 1);
    }
    else if (followUp === EContractPaymentRepetitionType.Yearly) {
        result = addYears(startOfYear(startDate), 1);
    }
    if (includingExtraDay) {
        return result;
    }
    // After adding a month the final day is in the next period.
    // Subtract 1 day to get the last day of the current pro-rata period.
    return subDays(result, 1);
};
export var getPagedId = function (params) {
    return getPagedIdBase(__assign(__assign({}, params), { entityName: "contract" }));
};
export var contractIndexGetIncrease = function (params) {
    var _a;
    var contract = params.contract, indexInfo = params.indexInfo, customNewPrice = params.customNewPrice;
    var currentPrice = contract.currentPrice;
    if (customNewPrice) {
        var base = 100;
        return (customNewPrice * base - currentPrice * base) / base;
    }
    return ((_a = getIndexInfoAvailable(indexInfo)) === null || _a === void 0 ? void 0 : _a.increase) || 0;
};
export var contractIndexGetNewPrice = function (params) {
    var _a;
    var contract = params.contract, indexInfo = params.indexInfo;
    var currentPrice = contract.currentPrice;
    return currentPrice + (((_a = getIndexInfoAvailable(indexInfo)) === null || _a === void 0 ? void 0 : _a.increase) || 0);
};
export var contractIndexGetMinNewPrice = function (contract) {
    var currentPrice = contract.currentPrice;
    return currentPrice;
};
export var getProRataExtraMonthEndDate = function (startDate, contract) {
    // Get followup
    var followUp = EContractPaymentRepetitionType.Monthly;
    if (contract === null || contract === void 0 ? void 0 : contract.repetitionType) {
        followUp = contract.repetitionType;
    }
    var base = getProRataBaseEndDate(startDate, contract, false);
    if (followUp === EContractPaymentRepetitionType.Quarterly) {
        return endOfMonth(addQuarters(base, 1));
    }
    if (followUp === EContractPaymentRepetitionType.Yearly) {
        return endOfMonth(addYears(base, 1));
    }
    return endOfMonth(addMonths(base, 1));
};
export var getIdentifierForProperty = function (propertyId, type) { return "property-".concat(type, "-").concat(propertyId).toLowerCase(); };
export var getFilterForProperty = function (propertyId, type) {
    switch (type) {
        case EContractFetchType.Active:
            return {
                and: [
                    {
                        contractType: { eq: EContractType.Basic },
                    },
                    {
                        propertyId: { eq: propertyId },
                    },
                    {
                        stopDate: {
                            gt: endOfDay(new Date()),
                        },
                    },
                ],
            };
        case EContractFetchType.Ended:
            return {
                and: [
                    {
                        contractType: { eq: EContractType.Basic },
                    },
                    {
                        propertyId: { eq: propertyId },
                    },
                    {
                        stopDate: {
                            lte: endOfDay(new Date()),
                        },
                    },
                ],
            };
        default:
            return {
                propertyId: propertyId,
            };
    }
};
export var getFilterForLinkedProperty = function (propertyId, type, includeLinkedProperty) {
    switch (type) {
        case EContractFetchType.Active:
            return {
                and: __spreadArray(__spreadArray([
                    {
                        contractType: { eq: EContractType.Basic },
                    }
                ], (includeLinkedProperty
                    ? [
                        {
                            linkedProperties: { linkedPropertyId: { eq: [propertyId] } },
                        },
                    ]
                    : []), true), [
                    // change end
                    {
                        stopDate: {
                            gt: endOfDay(new Date()),
                        },
                    },
                ], false),
            };
        case EContractFetchType.Ended:
            return {
                and: __spreadArray(__spreadArray([
                    {
                        contractType: { eq: EContractType.Basic },
                    }
                ], (includeLinkedProperty
                    ? [
                        {
                            linkedProperties: { linkedPropertyId: { eq: [propertyId] } },
                        },
                    ]
                    : []), true), [
                    // change end
                    {
                        stopDate: {
                            lte: endOfDay(new Date()),
                        },
                    },
                ], false),
            };
        default:
            return {
                propertyId: propertyId,
            };
    }
};
export var getIndexInfoAvailable = function (indexInfo) {
    if (!indexInfo || (indexInfo === null || indexInfo === void 0 ? void 0 : indexInfo.error)) {
        return;
    }
    return indexInfo;
};
export var getIndexInfoError = function (indexInfo) {
    return indexInfo === null || indexInfo === void 0 ? void 0 : indexInfo.error;
};
export var getContractCost = function (contract) {
    var _a = contract.currentPrice, currentPrice = _a === void 0 ? 0 : _a, _b = contract.priceVat, priceVat = _b === void 0 ? 0 : _b, _c = contract.currentCostsCommon, currentCostsCommon = _c === void 0 ? 0 : _c, _d = contract.costsCommonVat, costsCommonVat = _d === void 0 ? 0 : _d, _e = contract.currentCostsNonCommon, currentCostsNonCommon = _e === void 0 ? 0 : _e, _f = contract.costsNonCommonVat, costsNonCommonVat = _f === void 0 ? 0 : _f, _g = contract.amount, amount = _g === void 0 ? 0 : _g, contractType = contract.contractType, repetitionType = contract.repetitionType, _h = contract.additionalCosts, additionalCosts = _h === void 0 ? [] : _h;
    var hasAdditionalCosts = additionalCosts && additionalCosts.length > 0;
    switch (contractType) {
        case EContractType.RentDeposit:
            return formatCurrency(amount !== null && amount !== void 0 ? amount : 0);
        case EContractType.Basic:
        default: {
            var additionalCostsVats = hasAdditionalCosts
                ? additionalCosts === null || additionalCosts === void 0 ? void 0 : additionalCosts.map(function (item) {
                    return getVat((item === null || item === void 0 ? void 0 : item.currentPrice) || 0, item.priceVat / 100);
                })
                : [];
            var totalVatAmount = sum(__spreadArray([
                getVat(currentPrice, priceVat / 100),
                getVat(currentCostsCommon, costsCommonVat / 100),
                getVat(currentCostsNonCommon, costsNonCommonVat / 100)
            ], additionalCostsVats, true));
            var additionalCostsWithoutVat = hasAdditionalCosts
                ? additionalCosts === null || additionalCosts === void 0 ? void 0 : additionalCosts.map(function (item) { return item.currentPrice; })
                : [];
            var amountWithoutVat = formatCurrency(sum(__spreadArray([
                currentPrice,
                currentCostsCommon,
                currentCostsNonCommon
            ], additionalCostsWithoutVat, true)));
            // const total =
            //   totalVatAmount > 0
            //     ? getLocalizedText("system.contract.amount_with_vat", {
            //         amount: amountWithoutVat,
            //         vat: formatCurrency(totalVatAmount),
            //       })
            //     : amountWithoutVat;
            var result_1 = [
                getLocalizedText("system.contract.rent", {
                    amount: formatCurrency(currentPrice),
                }),
            ];
            if (currentCostsCommon) {
                result_1.push(getLocalizedText("system.contract.common_costs", {
                    cost: formatCurrency(currentCostsCommon),
                }));
            }
            if (currentCostsNonCommon) {
                result_1.push(getLocalizedText("system.contract.non_common_costs", {
                    cost: formatCurrency(currentCostsNonCommon),
                }));
            }
            if (additionalCosts === null || additionalCosts === void 0 ? void 0 : additionalCosts.length) {
                additionalCosts.forEach(function (item) {
                    var _a = item.currentPrice, currentPrice = _a === void 0 ? 0 : _a, _b = item.description, description = _b === void 0 ? "" : _b;
                    result_1.push(getLocalizedText("system.contract.additional_cost", {
                        description: description,
                        cost: formatCurrency(currentPrice),
                    }));
                });
            }
            var frequency = getLocalizedText("contract.payment_repetition.option.".concat((repetitionType || EContractPaymentRepetitionType.Monthly).toLowerCase())).toLowerCase();
            var total = totalVatAmount
                ? "".concat(amountWithoutVat, "  (").concat(formatCurrency(totalVatAmount), " ").concat(getLocalizedText("contract_info.vat_amount.title"), ")")
                : amountWithoutVat;
            return result_1.length > 1
                ? "".concat(result_1.join(" + "), " (Total = ").concat(total, ") ").concat(frequency)
                : "".concat(total, " ").concat(frequency);
        }
    }
};
export var getContractCostParts = function (contract) {
    var _a = contract.currentPrice, currentPrice = _a === void 0 ? 0 : _a, _b = contract.priceVat, priceVat = _b === void 0 ? 0 : _b, _c = contract.currentCostsCommon, currentCostsCommon = _c === void 0 ? 0 : _c, _d = contract.costsCommonVat, costsCommonVat = _d === void 0 ? 0 : _d, _e = contract.currentCostsNonCommon, currentCostsNonCommon = _e === void 0 ? 0 : _e, _f = contract.costsNonCommonVat, costsNonCommonVat = _f === void 0 ? 0 : _f, _g = contract.amount, amount = _g === void 0 ? 0 : _g, contractType = contract.contractType, repetitionType = contract.repetitionType, _h = contract.additionalCosts, additionalCosts = _h === void 0 ? [] : _h;
    if (contractType === EContractType.RentDeposit) {
        return { total: formatCurrency(amount !== null && amount !== void 0 ? amount : 0), breakdown: null };
    }
    var hasAdditionalCosts = additionalCosts && additionalCosts.length > 0;
    var additionalCostsVats = hasAdditionalCosts
        ? additionalCosts === null || additionalCosts === void 0 ? void 0 : additionalCosts.map(function (item) {
            return getVat((item === null || item === void 0 ? void 0 : item.currentPrice) || 0, item.priceVat / 100);
        })
        : [];
    var totalVatAmount = sum(__spreadArray([
        getVat(currentPrice, priceVat / 100),
        getVat(currentCostsCommon, costsCommonVat / 100),
        getVat(currentCostsNonCommon, costsNonCommonVat / 100)
    ], additionalCostsVats, true));
    var additionalCostsWithoutVat = hasAdditionalCosts
        ? additionalCosts === null || additionalCosts === void 0 ? void 0 : additionalCosts.map(function (item) { return item.currentPrice; })
        : [];
    var amountWithoutVat = formatCurrency(sum(__spreadArray([
        currentPrice,
        currentCostsCommon,
        currentCostsNonCommon
    ], additionalCostsWithoutVat, true)));
    var totalFormatted = totalVatAmount
        ? "".concat(amountWithoutVat, "  (").concat(formatCurrency(totalVatAmount), " ").concat(getLocalizedText("contract_info.vat_amount.title"), ")")
        : amountWithoutVat;
    var frequency = getLocalizedText("contract.payment_repetition.option.".concat((repetitionType || EContractPaymentRepetitionType.Monthly).toLowerCase())).toLowerCase();
    var result = [
        getLocalizedText("system.contract.rent", {
            amount: formatCurrency(currentPrice),
        }),
    ];
    if (currentCostsCommon) {
        result.push(getLocalizedText("system.contract.common_costs", {
            cost: formatCurrency(currentCostsCommon),
        }));
    }
    if (currentCostsNonCommon) {
        result.push(getLocalizedText("system.contract.non_common_costs", {
            cost: formatCurrency(currentCostsNonCommon),
        }));
    }
    if (additionalCosts === null || additionalCosts === void 0 ? void 0 : additionalCosts.length) {
        additionalCosts.forEach(function (item) {
            var _a = item.currentPrice, itemPrice = _a === void 0 ? 0 : _a, _b = item.description, description = _b === void 0 ? "" : _b;
            result.push(getLocalizedText("system.contract.additional_cost", {
                description: description,
                cost: formatCurrency(itemPrice),
            }));
        });
    }
    return {
        total: "".concat(totalFormatted, " ").concat(frequency),
        breakdown: result.length > 1 ? result.join(" + ") : null,
    };
};
export var checkIfEpcExpired = function (epcExpiryDate) {
    return epcExpiryDate && isBefore(epcExpiryDate, new Date());
};
export var getCanIndex = function (params) {
    var contract = params.contract, property = params.property;
    var indexInfoAvailable = getIndexInfoAvailable(contract.indexInfo);
    if (!indexInfoAvailable) {
        return false;
    }
    var epcExpired = checkIfEpcExpired(property.epcExpirationDate);
    var epcLabel = property.epcLabel || EPropertyEPCLabel.Unknown;
    var noEPC = [
        EPropertyEPCLabel.Unknown,
        EPropertyEPCLabel.NoEPCAvailable,
    ].includes(epcLabel);
    if (epcExpired || noEPC) {
        var propertyInFlanders = isFlemishProperty(property);
        var residentialLease = isResidentialLease(contract === null || contract === void 0 ? void 0 : contract.leaseType);
        if (!propertyInFlanders && residentialLease) {
            return true;
        }
    }
    switch (epcLabel) {
        case EPropertyEPCLabel.APlus:
        case EPropertyEPCLabel.A:
        case EPropertyEPCLabel.B:
        case EPropertyEPCLabel.C:
            return indexInfoAvailable.increase !== 0;
        case EPropertyEPCLabel.D:
            return indexInfoAvailable.increaseEpcD !== 0;
        case EPropertyEPCLabel.E:
            return indexInfoAvailable.increaseEpcE !== 0;
        case EPropertyEPCLabel.F:
            return indexInfoAvailable.increaseEpcF !== 0;
        case EPropertyEPCLabel.G:
            return indexInfoAvailable.increaseEpcG !== 0;
        case EPropertyEPCLabel.Unknown:
        case EPropertyEPCLabel.NoEPCAvailable:
        default:
            return indexInfoAvailable.increaseNoEpc !== 0;
    }
};
